import _ from 'lodash';
import { BackendService, GameConfigService, RpcService } from '~/rage';
import { IObservable, SeenTracker, Subject } from '~/rage/utils';
import { TattooCategory, WalletType } from '~/shared';
import { ITattooShopService, TattooItemConfig } from './tattooShopServiceTypes';

export class TattooShopService implements ITattooShopService {
	private items = new Subject<TattooItemConfig[]>([]);
	private ownedTattoos = new Subject<TattooItemConfig[]>([]);
	private tattooDeletionPrice = new Subject(0);
	private seenTracker = new SeenTracker('tattoo_shop');

	constructor(
		private readonly backend: BackendService,
		private readonly gameConfig: GameConfigService,
		private readonly rpc: RpcService,
	) {
		this.gameConfig.getConfigs().subscribe(() => {
			this.updateItems();

			const deletionPrice =
				this.gameConfig.configs?.tattoos?.tattooDeletionPrice;
			if (deletionPrice) {
				this.tattooDeletionPrice.next(deletionPrice);
			}
		});

		this.backend.addEventHandler(
			'tattoos:update',
			(payload: { tattooIds: string[] }) => {
				this.ownedTattoos.next(
					payload.tattooIds.map(
						(tattoo) =>
							this.items.getValue().find((item) => item.id === tattoo)!,
					),
				);
			},
		);
	}

	private updateItems(): void {
		if (!this.gameConfig.configs) {
			return;
		}

		const configItems = this.gameConfig.configs.tattoos?.items ?? [];
		this.items.next(configItems);
	}

	public getItems(): IObservable<TattooItemConfig[]> {
		return this.items;
	}

	public getOwnedTattoos(): IObservable<TattooItemConfig[]> {
		return this.ownedTattoos;
	}

	public async buyTattoo(
		tattooId: string,
		walletType: WalletType,
	): Promise<void> {
		await this.backend.invoke<void>('tattoos:buy', {
			tattooIds: _.castArray(tattooId),
			walletType,
		});
	}

	public async deleteTattoo(
		tattooId: string,
		walletType: WalletType,
	): Promise<void> {
		await this.backend.invoke<void>('tattoos:delete', {
			tattooIds: _.castArray(tattooId),
			walletType,
		});
	}

	public getTattooDeletionPrice(): Subject<number> {
		return this.tattooDeletionPrice;
	}

	public async previewTattoo(tattooId: string): Promise<void> {
		await this.rpc.invokeClient('tattooShop:previewTattoo', { tattooId });
	}

	public async previewTattooCategory(
		category: TattooCategory | 'delete' | null,
	): Promise<void> {
		await this.rpc.invokeClient('tattooShop:previewTattooCategory', {
			category,
		});
	}

	public isUnseen(path: string): boolean {
		return this.seenTracker.isUnseen(path);
	}

	public markSeen(path: string): void {
		this.seenTracker.markAsSeen(path);
	}

	public async exitMenu(): Promise<void> {
		this.rpc.invokeClient('tattooShop:exitMenu');
	}
}
